/**
 * The Quality screen's own API client.
 *
 * Beside the page rather than inside `lib/api.ts` for the reason the Studio's is: that interface is
 * implemented twice — once by the mock adapter that ships the demo and once by `httpApi` — so every
 * method added to it has to be added in two places, and four agents are writing in this repository
 * today. What it must not duplicate is the session's CSRF token, and it does not: `httpApi` owns it.
 *
 * Author: Danish Khan
 */
import { currentCsrf } from '../lib/httpApi.ts';
import { ApiError } from '../lib/apiTypes.ts';

const BASE = '/api/v1/quality';

export type Disposition = 'rule' | 'test' | 'wontfix';

export interface GuardRate {
  guard: string;
  /** Turns that REACHED this guard. The denominator the chart is drawn against. */
  turns: number;
  fires: number;
  ratePercent: number | null;
  byVerdict: { block: number; repair: number; fallback: number };
  lastFiredAt: string | null;
  silentSevenDays: boolean;
  neverReached: boolean;
}

export interface QaSummary {
  days: number;
  scorecardVersion: string;
  dimensions: { name: string; means: string }[];
  scored: number;
  failed: number;
  overall: number | null;
  means: Record<string, number | null>;
  flags: { flag: string; n: number }[];
  provenance: { scorecardVersion: string; promptVersion: string; model: string; n: number }[];
  byLanguage: { language: string; n: number; overall: number | null }[];
  costInr: number | null;
  tokensIn: number;
  tokensOut: number;
}

export interface Correction {
  id: string;
  turnId: string;
  conversationId: string;
  messageId: string | null;
  status: 'open' | 'closed';
  said: string;
  shouldHaveSaid: string;
  why: string | null;
  author: string;
  authorRole: string;
  disposition: Disposition | null;
  dispositionReason: string | null;
  proposalId: string | null;
  goldenCase: string | null;
  closedAt: string | null;
  closedBy: string | null;
  at: string;
  openHours: number | null;
}

export interface GoldenRun {
  id: string;
  ok: boolean;
  cases: number;
  casesFailed: number;
  assertions: number;
  assertionsFailed: number;
  digest: string | null;
  actor: string;
  source: string;
  durationMs: number | null;
  at: string;
  failed: { id: string; title: string; pins: string }[];
}

export interface PersonaRun {
  id: string;
  persona: string;
  mode: 'compliant' | 'defiant';
  turns: number;
  ok: boolean;
  checksFailed: number;
  storesUntouched: boolean;
  egressAttempts: number;
  failed: { name: string; detail: string }[];
  at: string;
}

export interface QualitySummary {
  guards: {
    days: number;
    denominator: string;
    zeroFireDays: number;
    rates: GuardRate[];
    silent: string[];
    neverReached: string[];
  };
  qa: QaSummary;
  thumbs: { days: number; up: number; down: number; raters: number; downWithCorrection: number };
  corrections: {
    open: number;
    closed: number;
    byDisposition: Record<string, number>;
    oldestOpenHours: number | null;
    ruleWithoutProposal: number;
    dispositions: { id: string; means: string }[];
  };
  golden: GoldenRun[];
  personas: PersonaRun[];
}

export interface Reply {
  turnId: string;
  conversationId: string;
  displayName: string;
  status: string;
  language: string | null;
  startedAt: string;
  customer: string[];
  bubbles: { id: string; text: string }[];
  guards: { guard: string; verdict: string }[];
  thumbs: { up: number; down: number };
  corrections: number;
  qa: { overall: number | null; outcome: string } | null;
}

export interface QaNight {
  localDay: string;
  population: number;
  target: number;
  picked: number;
  scored: number;
  failed: number;
  percent: number;
  skipped: string | null;
  costInr: number | null;
  tokensIn: number | null;
  tokensOut: number | null;
}

export interface QaScore {
  id: string;
  turnId: string;
  conversationId: string | null;
  outcome: 'scored' | 'unparseable' | 'unreadable' | 'unreachable';
  scores: Record<string, number> | null;
  overall: number | null;
  flags: string[];
  replyLanguage: string | null;
  failure: string | null;
  scorecardVersion: string;
  promptVersion: string;
  promptSha256: string;
  tier: string;
  model: string;
  costInr: number | null;
  at: string;
}

export interface QaView {
  settings: {
    percent: number;
    maxPercent: number;
    maxPerNight: number;
    hour: string;
    priced: boolean;
    scorecardVersion: string;
    flags: string[];
  };
  nights: QaNight[];
  scores: QaScore[];
  estimate: {
    population: number;
    target: number;
    percent: number;
    tokensIn: number;
    tokensOut: number;
    costInr: number | null;
    basis: string;
  };
}

export interface QualityClient {
  summary(): Promise<QualitySummary>;
  replies(filter: 'all' | 'flagged' | 'unrated'): Promise<Reply[]>;
  qa(): Promise<QaView>;
  corrections(status?: 'open' | 'closed'): Promise<Correction[]>;
  thumb(input: { turnId: string; messageId?: string | null; verdict: 'up' | 'down' }): Promise<void>;
  correct(input: { turnId: string; messageId?: string | null; shouldHaveSaid: string; why?: string }): Promise<Correction>;
  close(id: string, input: { disposition: Disposition; reason: string }): Promise<Correction>;
}

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  const method = init?.method ?? 'GET';
  const headers = new Headers(init?.headers);
  if (method !== 'GET') {
    // Only when there is a body: the app parses `application/json` with a raw parser, and a bodyless
    // POST that claims the type answers 400 as plain text. Learned in a browser by `httpApi`.
    if (init?.body !== undefined) headers.set('content-type', 'application/json');
    const csrf = currentCsrf();
    if (csrf) headers.set('x-csrf', csrf);
  }
  const res = await fetch(`${BASE}${path}`, { ...init, headers, credentials: 'same-origin' });
  const text = await res.text();
  let body: unknown = null;
  try { body = text ? JSON.parse(text) : null; } catch { /* handled below */ }
  if (!res.ok) {
    const detail = (body ?? {}) as { error?: { code?: string; message?: string } };
    // The API's own sentence, always. The disposition gate's refusal explains itself in full, and
    // replacing it with "Could not close correction" would throw away the only useful part.
    throw new ApiError(detail.error?.message ?? `${method} ${path} failed`, res.status,
      detail.error?.code ? { code: detail.error.code } : {});
  }
  return body as T;
}

const json = (value: unknown): RequestInit => ({ method: 'POST', body: JSON.stringify(value) });

export const qualityApi: QualityClient = {
  summary: () => call<QualitySummary>('/summary'),
  replies: async (filter) => (await call<{ replies: Reply[] }>(`/replies?filter=${filter}`)).replies,
  qa: () => call<QaView>('/qa'),
  corrections: async (status) =>
    (await call<{ corrections: Correction[] }>(`/corrections${status ? `?status=${status}` : ''}`)).corrections,
  thumb: async (input) => { await call('/thumbs', json(input)); },
  correct: async (input) => (await call<{ correction: Correction }>('/corrections', json(input))).correction,
  close: async (id, input) => (await call<{ correction: Correction }>(`/corrections/${id}/close`, json(input))).correction,
};

/* ------------------------------------------------------------------ small helpers */

/** `4 of 100 turns (4.0%)`. The denominator is never dropped, which is the whole rule. */
export function rateLabel(g: GuardRate): string {
  if (g.neverReached) return 'nothing reached it';
  return `${g.fires} of ${g.turns} turns (${(g.ratePercent ?? 0).toFixed(1)}%)`;
}

/** Hours, as a person says them. Used for how long a correction has been open. */
export function hoursLabel(hours: number | null): string {
  if (hours === null) return '—';
  if (hours < 1) return 'under an hour';
  if (hours < 48) return `${Math.round(hours)} h`;
  return `${Math.round(hours / 24)} d`;
}
